import { UseQueryResult } from '@edreamsodigeo/frontend-api-client';
import {
  ItinerarySearchQuery,
  SearchQueryQueryVariables,
  Maybe,
} from 'generated/graphql';
import type { SearchQueryResult } from './types';

type ItinerarySearchResponse = UseQueryResult<
  ItinerarySearchQuery,
  SearchQueryQueryVariables
>;
export const arrayToMap = <T extends { id: string }, K extends keyof T>(
  array: T[],
  attributeName: K
): Record<string, T[typeof attributeName]> => {
  return array.reduce((acc, item) => {
    acc[item.id] = item[attributeName];
    return acc;
  }, {});
};

export const getCommercialCarrier = (
  hasCommercialCarriers: boolean,
  carrierId: string,
  commercialCarrierId: Maybe<string> | undefined
) => {
  const isRenfe = carrierId === '4R';
  return hasCommercialCarriers && isRenfe && commercialCarrierId
    ? commercialCarrierId
    : carrierId;
};

export const transformItinerarySearchResponse = ({
  data,
  hasCommercialCarriers = false,
}: {
  data: ItinerarySearchResponse['data'];
  hasCommercialCarriers?: boolean;
}): SearchQueryResult['data'] => {
  const { itinerarySearch } = data || {};
  if (!itinerarySearch) return null;

  const {
    insuranceOffers,
    carbonFootprints,
    campaignConfigs,
    locations,
    carriers,
    priceTypeDisplayed,
    fees,
    ...rest
  } = itinerarySearch;
  // Turn arrays into id maps so we can easily access them by id instead
  // of iterating through the array every time we need a value
  const itinerarySearchMap = {
    ...itinerarySearch,
    fees: arrayToMap(fees || [], 'type'),
    locations: arrayToMap(locations || [], 'location'),
    carriers: arrayToMap(carriers || [], 'carrier'),
    insuranceOffers: arrayToMap(insuranceOffers || [], 'insuranceOffer'),
    carbonFootprints: arrayToMap(carbonFootprints || [], 'carbonFootprint'),
    campaignConfigs: arrayToMap(campaignConfigs || [], 'campaignConfig'),
  };

  return {
    search: {
      ...rest,
      __typename: 'SearchResponse',
      defaultFeeType: { id: priceTypeDisplayed as string },
      itineraries: itinerarySearch?.itineraries.map((itinerary) => {
        const { campaignConfigId, carbonFootprintId, ...rest } = itinerary;

        return {
          ...rest,
          __typename: 'Itinerary',
          fees: rest.fees.map((fee) =>
            itinerarySearchMap?.fees?.[fee.type]
              ? {
                  price: fee.price,
                  type: itinerarySearchMap.fees[fee.type],
                }
              : {
                  price: fee.price,
                  type: { id: fee.type },
                }
          ),
          campaignConfig:
            itinerarySearchMap.campaignConfigs[campaignConfigId as string],
          carbonFootprint:
            itinerarySearchMap.carbonFootprints[carbonFootprintId as string],
          legs: itinerary.legs?.map((leg) => ({
            ...leg,
            __typename: 'Leg',
            segments: leg.segments.map((segment) => {
              const { carrierId, ...rest } = segment;
              let commercialBrandSegment;
              return {
                ...rest,
                __typename: 'Segment',
                sections: segment.sections?.map((section) => {
                  const {
                    departureId,
                    destinationId,
                    carrierId,
                    operatingCarrierId,
                    commercialBrandId,
                    insuranceOfferId,
                    ...rest
                  } = section;
                  commercialBrandSegment = commercialBrandId;
                  return {
                    ...rest,
                    __typename: 'Section',
                    insuranceOffer:
                      insuranceOfferId != null
                        ? itinerarySearchMap.insuranceOffers[insuranceOfferId]
                        : null,
                    departure: itinerarySearchMap.locations[departureId],
                    destination: itinerarySearchMap.locations[destinationId],
                    carrier:
                      itinerarySearchMap.carriers[
                        getCommercialCarrier(
                          hasCommercialCarriers,
                          carrierId,
                          commercialBrandId
                        )
                      ],
                    operatingCarrier:
                      itinerarySearchMap.carriers[operatingCarrierId as string],
                    commercialBrand:
                      itinerarySearchMap.carriers[commercialBrandId as string],
                  };
                }),
                carrier:
                  itinerarySearchMap.carriers[
                    getCommercialCarrier(
                      hasCommercialCarriers,
                      carrierId,
                      commercialBrandSegment
                    )
                  ],
              };
            }),
          })),
        };
      }),
    },
  };
};
